import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import i18n from "./locale/i18n";
import { I18nextProvider } from "react-i18next";
import { App as AntdApp, ConfigProvider, Flex, Spin, theme } from "antd";
import { CookiesProvider } from "react-cookie";
import { useThemeContext } from "./context/theme/context";
import { useAuthenticate } from "./hooks/useAuthenticate";
import NotAuthorizedPage from "./pages/403";
import { AppRoute } from "./Routes";
import enUS from 'antd/locale/en_US';
import arEG from 'antd/locale/ar_EG';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';

const { darkAlgorithm, defaultAlgorithm } = theme;

function App() {
  const { state } = useThemeContext();
  const { loading, authenticated } = useAuthenticate();
  const location = useLocation();

  // Check if current path is a public route that should be accessible without authentication
  const isPublicRoute = location.pathname.startsWith("/invite/") || 
                        location.pathname.startsWith("/login") ||
                        location.pathname.startsWith("/forgot-password") ||
                        location.pathname.startsWith("/reset-password/");

  const antdLocale = useMemo(() => {
    return state.language === 'ar' ? arEG : enUS;
  }, [state.language]);

  useEffect(() => {
    const html = document.documentElement;
    if (state.mode === "dark") html.style.backgroundColor = "#141414";
    else html.style.backgroundColor = "#ffffff";

    return () => {
      html.style.backgroundColor = "";
    };
  }, [state.mode]);

  useEffect(() => {
    // Configure dayjs locale based on current language
    dayjs.locale(state.language === 'ar' ? 'ar' : 'en');
  }, [state.language]);

  if (loading) {
    return (
      <Flex justify="center" align="center">
        <Spin size="large" />
      </Flex>
    );
  }
  // Allow public routes to be accessed even without authentication
  if (!authenticated && !isPublicRoute) {
    return <NotAuthorizedPage />;
  }
  // TODO add interceptor for any error code 405 to show permission denied message
  return (
    <div style={{ width: "100%", maxWidth: "100vw", overflowX: "hidden" }}>
      <CookiesProvider>
        <I18nextProvider i18n={i18n}>
          <ConfigProvider
              locale={antdLocale}
            direction={i18n.dir()}
            theme={{
              token: {
                colorPrimary: "#FAB92B",
                colorSuccess: "#9FF431",
                colorWarning: "#F9D22D",
                colorError: "#F94E3D",
                colorLink: "#33AAF2",
                colorInfo: "#33AAF2",
                fontSize: 14, // don't work on light theme
              },
              algorithm: state.mode === "dark" ? darkAlgorithm : defaultAlgorithm,
              components: {
                Tabs: {
                  margin: 0,
                },
              },
            }}
          >
            <AntdApp>
              <AppRoute />
            </AntdApp>
          </ConfigProvider>
        </I18nextProvider>
      </CookiesProvider>
    </div>
  );
}

export default App;
